"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.SqlClient = void 0;
var models_1 = require("src/models");
var helpers_1 = require("src/helpers");
var errors_1 = require("src/errors");
var clients_1 = require("src/clients");
/**
 * SDK for generating the properly formatted query strings based on the API documented
 * at [developer.domo.com](https://developer.domo.com/docs/dev-studio-references/data-api#SQL%20API).
 * This is the default export of the library.
 * ### Example Usage
 *
 * ```js
 * import { SqlClient as Query } from '@domoinc/toolkit';
 *
 * const datasetAlias = 'dsAlias';
 *
 * (new Query())
 *  .get('alias', `select * from ${datasetalias}`)
 *  .then((data) => {
 *    // do something with the data
 *    console.table(data);
 *  });
 * ```
 */
var SqlClient = /** @class */ (function () {
    function SqlClient() {
    }
    SqlClient.parsePageFilters = function (datasets, produceClauses) {
        if (produceClauses === void 0) { produceClauses = false; }
        var mappedFilters = SqlClient.mapFilters();
        return datasets.reduce(function (acc, next) {
            var datasetFilters = mappedFilters[next];
            if (datasetFilters !== undefined && datasetFilters.length > 0) {
                var predicateBlocks = datasetFilters
                    .map(function (x) { return ({
                    aggregate: Boolean(x.aggregated),
                    predicate: SqlClient.filterToSQLPredicate(x),
                }); })
                    .filter(function (_a) {
                    var predicate = _a.predicate;
                    return (predicate === undefined ? '' : predicate).length > 0;
                });
                acc[next] = produceClauses
                    ? SqlClient.sqlPredicatesToClauses(predicateBlocks)
                    : predicateBlocks;
            }
            return acc;
        }, {});
    };
    /**
     * A helper method for generating a GET request. This method also
     * enables caching requests.
     *    @param {Boolean} useV2Endpoint If set to true, the v2 endpoint will be hit.
     *    Use this endpoint when you need the column names to refer to aliases if your app's manifest.
     */
    SqlClient.prototype.get = function (alias, query) {
        if (!(0, helpers_1.isString)(alias) || !(0, helpers_1.isString)(query)) {
            throw new errors_1.InvalidParamError();
        }
        var url = models_1.EndpointsV2.sql.replace('{alias}', alias);
        var headers = {
            'content-type': models_1.DataFormatTypes.plain,
            accept: models_1.DataFormatTypes.json,
        };
        return clients_1.TransportClient.post(url, query, headers);
    };
    /**
     * @ignore
     */
    SqlClient.sqlOperationParsers = (_a = {},
        _a[models_1.FilterOperand.In] = function (columnName, filter) {
            return SqlClient.buildINClause(columnName, filter);
        },
        _a[models_1.FilterOperand.NotIn] = function (columnName, filter) {
            return SqlClient.buildINClause(columnName, filter, true);
        },
        _a[models_1.FilterOperand.GreaterThan] = function (columnName, filter) {
            return "".concat(columnName, " > ").concat(SqlClient.getExpressionValue(filter.dataType, filter.values[0]));
        },
        _a[models_1.FilterOperand.GreaterThanOrEqualsTo] = function (columnName, filter) {
            return "".concat(columnName, " >= ").concat(SqlClient.getExpressionValue(filter.dataType, filter.values[0]));
        },
        _a[models_1.FilterOperand.LessThan] = function (columnName, filter) {
            return "".concat(columnName, " < ").concat(SqlClient.getExpressionValue(filter.dataType, filter.values[0]));
        },
        _a[models_1.FilterOperand.LessThanOrEqualsTo] = function (columnName, filter) {
            return "".concat(columnName, " <= ").concat(SqlClient.getExpressionValue(filter.dataType, filter.values[0]));
        },
        _a[models_1.FilterOperand.Between] = function (columnName, filter) {
            if (filter.values.length < 2) {
                return '';
            }
            return "(".concat(columnName, " >= ").concat(SqlClient.getExpressionValue(filter.dataType, filter.values[0]), " AND ").concat(columnName, " <= ").concat(SqlClient.getExpressionValue(filter.dataType, filter.values[1]), ")");
        },
        _a[models_1.FilterOperand.Equals] = function (columnName, filter) {
            return "".concat(columnName, " = ").concat(SqlClient.getExpressionValue(filter.dataType, filter.values[0]));
        },
        _a[models_1.FilterOperand.NotEquals] = function (columnName, filter) {
            return "".concat(columnName, " != ").concat(SqlClient.getExpressionValue(filter.dataType, filter.values[0]));
        },
        _a);
    /**
     * @ignore
     */
    SqlClient.mapFilters = function () {
        var filters = clients_1.DomoClient.getPageFilters();
        if (filters === undefined || filters === null)
            return {};
        return filters.reduce(function (acc, next) {
            var dataSourceId = next.dataSourceId;
            if (acc[dataSourceId] === undefined) {
                acc[dataSourceId] = [];
            }
            acc[dataSourceId].push(next);
            return acc;
        }, {});
    };
    SqlClient.escapeSingleQuote = function (value) {
        return value.replaceAll("'", "''");
    };
    /**
     * @ignore
     */
    SqlClient.joinINValues = function (filter) {
        if (filter.values === undefined)
            return '';
        var values = filter.values.filter(function (x) {
            // there's a bug in page filters where the NULL value is transformed
            // to the string-type word 'NaN' after the page filter is applied,
            // and that same word is passed to the card as an actual applied filter.
            if (filter.dataType !== models_1.FilterDataType.String && x === 'NaN') {
                return false;
            }
            return x !== null;
        });
        if (values.length === 0)
            return '';
        switch (filter.dataType) {
            case models_1.FilterDataType.String:
            case models_1.FilterDataType.Date:
            case models_1.FilterDataType.Datetime: {
                return values
                    .map(function (x) { return "'".concat(SqlClient.escapeSingleQuote(x.toString()), "'"); })
                    .join(',');
            }
            case models_1.FilterDataType.Numeric: {
                return values.join(',');
            }
        }
    };
    /**
     * @ignore
     */
    SqlClient.getExpressionValue = function (dataType, value) {
        if (dataType === void 0) { dataType = models_1.FilterDataType.String; }
        switch (dataType) {
            case models_1.FilterDataType.String:
            case models_1.FilterDataType.Date:
            case models_1.FilterDataType.Datetime: {
                return "'".concat(SqlClient.escapeSingleQuote(value.toString()), "'");
            }
            case models_1.FilterDataType.Numeric: {
                return value;
            }
        }
    };
    /**
     * @ignore
     */
    SqlClient.buildINClause = function (columnName, filter, notIn) {
        if (notIn === void 0) { notIn = false; }
        // "NaN": see comment above, in "joinINValues" method
        var hasNull = filter.values.some(function (x) {
            return x === null ||
                (filter.dataType !== models_1.FilterDataType.String && x === 'NaN');
        });
        var inOperand = notIn ? 'NOT IN' : 'IN';
        var predicateIN = SqlClient.joinINValues(filter);
        var hasPredicateIN = predicateIN.length > 0;
        var clauseIN = hasPredicateIN
            ? "".concat(columnName, " ").concat(inOperand, " (").concat(predicateIN, ")")
            : '';
        if (hasNull) {
            var nullOperand = notIn ? 'IS NOT' : 'IS';
            var andOr = notIn ? 'AND' : 'OR';
            return "(".concat(hasPredicateIN ? "".concat(clauseIN, " ").concat(andOr) : '', " ").concat(columnName, " ").concat(nullOperand, " NULL)");
        }
        return clauseIN;
    };
    /**
     * @ignore
     */
    SqlClient.filterToSQLPredicate = function (filter) {
        if (filter.operand !== undefined) {
            var parser = SqlClient.sqlOperationParsers[filter.operand];
            if (parser === undefined) {
                return '';
            }
            return parser(Boolean(filter.aggregated) && filter.aggregation !== undefined
                ? "".concat(filter.aggregation, "(\"").concat(filter.column, "\")")
                : "\"".concat(filter.column, "\""), filter);
        }
        return '';
    };
    /**
     * @ignore
     */
    SqlClient.sqlPredicatesToClauses = function (predicateBlocks) {
        var whereClause = '';
        var havingClause = '';
        var whereClauseCounter = 0;
        var havingClauseCounter = 0;
        predicateBlocks.forEach(function (block) {
            if (block.aggregate) {
                havingClause += "".concat(havingClauseCounter > 0 ? ' AND ' : '').concat(block.predicate);
                havingClauseCounter += 1;
            }
            else {
                whereClause += "".concat(whereClauseCounter > 0 ? ' AND ' : '').concat(block.predicate);
                whereClauseCounter += 1;
            }
        });
        return {
            where: whereClauseCounter > 0 ? whereClause : undefined,
            having: havingClauseCounter > 0 ? havingClause : undefined,
        };
    };
    return SqlClient;
}());
exports.SqlClient = SqlClient;
